import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useOrderStore } from '../../store/orderStore'
import { useUIStore } from '../../store/uiStore'
import api from '../../api/axiosConfig'
import './CompletePayment.css'
import SuccessScreen from './SuccessScreen'
import FailureScreen from './FailureScreen'
import SupportModal from './SupportModal'
import { TREASURY_ADDRESSES } from '../../lib/constants/contracts'



interface ManualPaymentIntent {
    quote: any
    signature: string
    paymentAddress: string
    paymentAmount: number
    paymentAsset: string
    expiresAt: number
}

const getNetworkIcon = (network: string): string => {
    const n = network.toLowerCase()
    if (n.includes('bitcoin') || n.includes('btc')) return '₿'
    if (n.includes('ethereum') || n.includes('eth') || n.includes('erc20')) return 'Ξ'
    if (n.includes('tron') || n.includes('trc20')) return '◎'
    if (n.includes('polygon') || n.includes('matic')) return '⬡'
    if (n.includes('solana') || n.includes('sol')) return '◉'
    if (n.includes('usdc')) return '💵'
    return '🔗'
}

/**
 * Get display-friendly asset name from paymentAsset
 */
const getAssetDisplayName = (asset: string): string => {
    if (!asset) return 'USDT'
    const a = asset.toUpperCase()
    if (a === 'BTC' || a.includes('BITCOIN')) return 'BTC'
    if (a === 'SOL' || a.includes('SOLANA')) return 'SOL'
    if (a === 'ETH') return 'ETH'
    if (a.includes('USDT')) return 'USDT'
    if (a.includes('USDC')) return 'USDC'
    return asset.replace(/-/g, ' ')
}



export default function CompletePayment() {
    const { selectedPackage, developerAccess, getTotalPrice, walletAddress, network, setSelectedPackage, setDeveloperAccess, setWalletAddress, setNetwork } = useOrderStore()
    const { setCurrentBoard } = useUIStore()
    const [isProcessing, setIsProcessing] = useState(false)
    const [error, setError] = useState('')
    const [copied, setCopied] = useState<string | null>(null)

    // Manual payment state
    const [manualIntent, setManualIntent] = useState<ManualPaymentIntent | null>(null)
    const [txHash, setTxHash] = useState('')
    const [isVerifying, setIsVerifying] = useState(false)
    const [verificationStatus, setVerificationStatus] = useState<'idle' | 'verifying' | 'verified' | 'failed'>('idle')
    const [timeLeft, setTimeLeft] = useState<number>(0)
    const [selectedChain, setSelectedChain] = useState<'TRON' | 'ETHEREUM' | 'SOLANA' | 'BITCOIN' | 'POLYGON'>('TRON')
    const [showSupportModal, setShowSupportModal] = useState(false)

    const chains = [
        { id: 'TRON', name: 'USDT (TRC20)', network: 'Tron' },
        { id: 'ETHEREUM', name: 'USDT (ERC20)', network: 'Ethereum' },
        { id: 'POLYGON', name: 'USDT (Polygon)', network: 'Polygon' },
        { id: 'SOLANA', name: 'USDT (Solana)', network: 'Solana' },
        { id: 'BITCOIN', name: 'Bitcoin (BTC)', network: 'Bitcoin' }
    ]

    // Load order data from localStorage
    useEffect(() => {
        try {
            const orderDataStr = localStorage.getItem('flashusdt:order_data')
            if (orderDataStr) {
                const orderData = JSON.parse(orderDataStr)
                if (!selectedPackage && orderData.packageId) {
                    setSelectedPackage({
                        id: orderData.packageId,
                        name: orderData.packageName,
                        amount: orderData.packageAmount,
                        price: orderData.packagePrice,
                    })
                }
                if (!developerAccess && orderData.devAccessId) {
                    setDeveloperAccess({
                        id: orderData.devAccessId,
                        name: orderData.devAccessName,
                        price: orderData.devAccessPrice,
                        amount: 0
                    })
                }
                if (!walletAddress && orderData.walletAddress) {
                    setWalletAddress(orderData.walletAddress)
                }
                if (!network && orderData.network) {
                    setNetwork(orderData.network)
                }
            }
        } catch {
            // ignore parse errors
        }
    }, [])

    // Create Manual Intent Automatically on Mount (if data is ready)
    useEffect(() => {
        const initManualPayment = async () => {
            if (!manualIntent && (selectedPackage || developerAccess) && walletAddress && !isProcessing && !error) {
                // Check if we already have an intent? (Maybe not needed if stateless)
                // Just create one.
                handleManualCheckout()
            }
        }

        // Short delay to ensure state hydration
        const timer = setTimeout(initManualPayment, 500)
        return () => clearTimeout(timer)
    }, [selectedPackage, developerAccess, walletAddress, manualIntent])


    // Countdown timer for manual payment
    useEffect(() => {
        if (!manualIntent) return

        const updateTimer = () => {
            const remaining = manualIntent.expiresAt - Date.now()
            setTimeLeft(Math.max(0, remaining))
        }

        updateTimer()
        const interval = setInterval(updateTimer, 1000)
        return () => clearInterval(interval)
    }, [manualIntent])


    const handleCopyToClipboard = async (text: string, field: string) => {
        try {
            await navigator.clipboard.writeText(text)
            setCopied(field)
            setTimeout(() => setCopied(null), 2000)
        } catch {
            // ignore
        }
    }

    const handleManualCheckout = async () => {
        // Validate required fields before making the API call
        if ((!selectedPackage && !developerAccess) || !walletAddress) {
            // Wait for hydration or redirect?
            return
        }

        if (!network || !network.trim()) {
            setError('Please select a network for delivery')
            return
        }

        setIsProcessing(true)
        setError('')

        try {
            // Derive payment asset from network if not explicitly set
            const getPaymentAsset = (net: string): string => {
                const n = net.toLowerCase()
                if (n.includes('bitcoin') || n.includes('btc')) return 'BTC'
                if (n.includes('ethereum') || n.includes('eth') || n.includes('erc20')) return 'USDT-ERC20'
                if (n.includes('tron') || n.includes('trc20')) return 'USDT-TRC20'
                if (n.includes('solana') || n.includes('sol')) return 'SOL'
                return 'USDT-TRC20' // Default
            }

            const asset = getPaymentAsset(network)

            const payload: {
                items?: Array<{ productId: string; quantity: number }>;
                customOptions?: { amount?: number };
                deliveryAddress: string;
                deliveryNetwork: string;
                paymentAsset: string;
            } = {
                deliveryAddress: walletAddress,
                deliveryNetwork: network,
                paymentAsset: asset,
            }

            const items = []

            if (selectedPackage) {
                if (selectedPackage.id !== 'custom') {
                    items.push({ productId: selectedPackage.id, quantity: 1 })
                } else {
                    payload.customOptions = { amount: selectedPackage.amount }
                }
            }

            if (developerAccess) {
                items.push({ productId: developerAccess.id, quantity: 1 })
            }

            if (items.length > 0) {
                payload.items = items
            }

            const response = await api.post<ManualPaymentIntent>(
                '/api/payments/manual/create-intent',
                payload
            )



            setManualIntent(response.data)
            setIsProcessing(false)
        } catch (err: any) {
            const errorMessage = err.response?.data?.message ||
                err.response?.data?.error ||
                err.message ||
                'Failed to create manual payment intent'
            setError(errorMessage)
            setIsProcessing(false)
        }
    }

    const handleVerifyTransaction = async () => {
        if (!manualIntent || !txHash.trim()) {
            setError('Please enter transaction hash')
            return
        }

        setIsVerifying(true)
        setError('')
        setVerificationStatus('verifying')

        try {
            const response = await api.post('/api/payments/manual/verify-tx', {
                signedQuote: {
                    quote: manualIntent.quote,
                    signature: manualIntent.signature,
                },
                txHash: txHash.trim(),
            })

            if (response.data.status === 'verified' && response.data.fulfilled) {
                setVerificationStatus('verified')
            } else if (response.data.status === 'already_processed') {
                setVerificationStatus('verified')
            } else {
                setVerificationStatus('failed')
                setError('Verification failed. Please check your transaction hash.')
            }
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to verify transaction')
            setVerificationStatus('failed')
        } finally {
            setIsVerifying(false)
        }
    }

    const formatTime = (ms: number) => {
        const totalSeconds = Math.floor(ms / 1000)
        if (totalSeconds <= 0) return '00:00'
        const mins = Math.floor(totalSeconds / 60)
        const secs = totalSeconds % 60
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
    }



    // Success screen
    if (verificationStatus === 'verified' && (selectedPackage || developerAccess)) {
        return (
            <SuccessScreen
                txHash={txHash}
                amount={selectedPackage?.amount}
                network={network}
                onReset={() => {
                    setCurrentBoard('ORDER')
                    window.history.replaceState({}, '', '/')
                }}
            />
        )
    }

    // Failure screen
    if (verificationStatus === 'failed') {
        return (
            <>
                <FailureScreen
                    error={error || 'Payment verification failed'}
                    onRetry={() => {
                        setVerificationStatus('idle')
                        setError('')
                        setTxHash('')
                    }}
                    onContactSupport={() => setShowSupportModal(true)}
                />
                <SupportModal isOpen={showSupportModal} onClose={() => setShowSupportModal(false)} />
            </>
        )
    }

    return (
        <div className="complete-payment-page">
            <div className="payment-background">
                <div className="bg-gradient-1"></div>
                <div className="bg-gradient-2"></div>
                <div className="bg-grid"></div>
            </div>

            <motion.div
                className="complete-payment-container"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, ease: 'easeOut' }}
            >
                {/* Header */}
                <div className="payment-page-header relative">


                    <motion.div
                        className="header-icon"
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
                    >
                        <span className="flash-icon">⚡</span>
                    </motion.div>
                    <h1 className="payment-page-title">Complete Payment</h1>
                    <p className="payment-page-subtitle">Send crypto to the address below</p>
                </div>

                {/* Order Summary Card */}
                {(selectedPackage || developerAccess) && (
                    <motion.div
                        className="order-summary-card"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.1 }}
                    >
                        <div className="summary-header">
                            <span className="summary-badge">Order Summary</span>
                        </div>
                        <div className="summary-content">
                            {selectedPackage && (
                                <>
                                    <div className="summary-row">
                                        <span className="summary-label">Package</span>
                                        <span className="summary-value highlight">{selectedPackage.name}</span>
                                    </div>
                                    <div className="summary-row">
                                        <span className="summary-label">Amount</span>
                                        <span className="summary-value">
                                            <span className="usdt-icon">₮</span>
                                            {selectedPackage.amount.toLocaleString()}
                                        </span>
                                    </div>
                                    <div className="summary-row">
                                        <span className="summary-label">Price</span>
                                        <span className="summary-value text-gray-400">${selectedPackage.price.toLocaleString()}</span>
                                    </div>
                                </>
                            )}

                            {developerAccess && (
                                <div className="mt-2 pt-2 border-t border-white/5">
                                    <div className="summary-row">
                                        <span className="summary-label">Add-on</span>
                                        <span className="summary-value highlight text-neon-blue">{developerAccess.name}</span>
                                    </div>
                                    <div className="summary-row">
                                        <span className="summary-label">Price</span>
                                        <span className="summary-value text-gray-400">${developerAccess.price.toLocaleString()}</span>
                                    </div>
                                </div>
                            )}

                            <div className="summary-divider"></div>
                            <div className="summary-row total">
                                <span className="summary-label">Total To Pay</span>
                                <span className="summary-value price">${getTotalPrice().toLocaleString()}</span>
                            </div>

                            <div className="summary-row mt-2">
                                <span className="summary-label">Network</span>
                                <span className="summary-value network-badge">
                                    <span className="network-icon">{getNetworkIcon(network || '')}</span>
                                    {network}
                                </span>
                            </div>
                        </div>
                    </motion.div>
                )}

                {/* Manual Payment Content */}
                {isProcessing && !manualIntent ? (
                    <div className="loading-state">
                        <span className="spinner"></span>
                        <p>Generating secure payment address...</p>
                    </div>
                ) : manualIntent ? (
                    <AnimatePresence mode="wait">
                        <motion.div
                            key="manual-payment"
                            className="manual-payment-flow"
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                        >
                            {/* Timer Card */}
                            <div className={`timer-card ${timeLeft < 120000 ? 'urgent' : ''}`}>
                                <div className="timer-icon-wrapper">
                                    <span className="timer-icon">⏱️</span>
                                </div>
                                <div className="timer-content">
                                    <span className="timer-label">Time Remaining</span>
                                    <span className="timer-value">{formatTime(timeLeft)}</span>
                                </div>
                                {timeLeft < 120000 && (
                                    <span className="timer-warning">Complete payment soon!</span>
                                )}
                            </div>

                            {/* Payment Details */}
                            <div className="payment-details-card">
                                <h2 className="details-title">
                                    <span className="title-icon">📤</span>
                                    Send Payment
                                </h2>

                                {/* Network Pills */}
                                <div className="network-selector-container">
                                    <div className="mini-chain-selector">
                                        {chains.map((chain) => (
                                            <button
                                                key={chain.id}
                                                className={`mini-chain-pill ${selectedChain === chain.id ? 'active' : ''}`}
                                                onClick={() => setSelectedChain(chain.id as any)}
                                                title={chain.name}
                                            >
                                                {chain.id === 'TRON' ? 'USDT-TRC20' :
                                                    chain.id === 'ETHEREUM' ? 'USDT-ERC20' :
                                                        chain.id === 'POLYGON' ? 'USDT-POLY' :
                                                            chain.id === 'SOLANA' ? 'SOL' : 'BTC'}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <div className="payment-field">
                                    <label className="field-label">Send exactly</label>
                                    <div className="amount-display-box">
                                        <span className="crypto-amount">{manualIntent.paymentAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 8 })}</span>
                                        <span className="crypto-asset">{getAssetDisplayName(manualIntent.paymentAsset)}</span>
                                        <button
                                            className={`copy-button ${copied === 'amount' ? 'copied' : ''}`}
                                            onClick={() => handleCopyToClipboard(manualIntent.paymentAmount.toString(), 'amount')}
                                        >
                                            {copied === 'amount' ? '✓' : '📋'}
                                        </button>
                                    </div>
                                </div>

                                <div className="payment-field">
                                    <label className="field-label">To this address</label>
                                    <div className="address-display-box">
                                        <code className="wallet-address">{TREASURY_ADDRESSES[selectedChain]}</code>
                                        <button
                                            className={`copy-button ${copied === 'address' ? 'copied' : ''}`}
                                            onClick={() => handleCopyToClipboard(TREASURY_ADDRESSES[selectedChain], 'address')}
                                        >
                                            {copied === 'address' ? '✓' : '📋'}
                                        </button>
                                    </div>
                                </div>

                                <div className="payment-warning">
                                    <span className="warning-icon">⚠️</span>
                                    <span>Send exact amount on the correct network. Incorrect transfers cannot be recovered.</span>
                                </div>

                                {/* Privacy Disclaimer */}
                                <div className="privacy-disclaimer">
                                    <div className="privacy-icon-wrapper">
                                        <span className="privacy-icon">🛡️</span>
                                    </div>
                                    <div className="privacy-text">
                                        <strong>Recommended for Anonymity</strong>
                                        <span>Manual transfers are executed strictly peer-to-peer. No metadata is retained, ensuring your transaction remains 100% untraceable and private.</span>
                                    </div>
                                </div>
                            </div>

                            {/* Transaction Verification */}
                            <div className="verification-card">
                                <h3 className="verification-title">
                                    <span className="title-icon">🔍</span>
                                    Verify Your Payment
                                </h3>
                                <p className="verification-desc">
                                    After sending, paste your transaction hash below
                                </p>

                                <div className="tx-input-wrapper">
                                    <input
                                        type="text"
                                        className="tx-hash-input"
                                        placeholder="0x... or transaction ID"
                                        value={txHash}
                                        onChange={(e) => setTxHash(e.target.value)}
                                        disabled={isVerifying || verificationStatus === 'verified'}
                                    />
                                    <button
                                        className={`verify-button ${isVerifying ? 'verifying' : ''}`}
                                        onClick={handleVerifyTransaction}
                                        disabled={!txHash.trim() || isVerifying || verificationStatus === 'verified'}
                                    >
                                        {isVerifying ? (
                                            <>
                                                <span className="loading-spinner small"></span>
                                                <span>Verifying...</span>
                                            </>
                                        ) : verificationStatus === 'verified' ? (
                                            <>
                                                <span>✓</span>
                                                <span>Verified</span>
                                            </>
                                        ) : (
                                            <span>Verify Payment</span>
                                        )}
                                    </button>
                                </div>

                                {/* Error Display */}
                                <AnimatePresence>
                                    {error && (
                                        <motion.div
                                            className="error-banner small"
                                            initial={{ opacity: 0, height: 0 }}
                                            animate={{ opacity: 1, height: 'auto' }}
                                            exit={{ opacity: 0, height: 0 }}
                                        >
                                            <span className="error-icon">⚠️</span>
                                            <span className="error-text">{error}</span>
                                        </motion.div>
                                    )}
                                </AnimatePresence>

                                {verificationStatus === 'verifying' && (
                                    <div className="verification-progress">
                                        <div className="progress-spinner"></div>
                                        <span>Checking blockchain for your transaction...</span>
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    </AnimatePresence>
                ) : (
                    // Initial Error State (failed to create intent)
                    error && !isProcessing && (
                        <div className="error-state">
                            <p className="error-text">{error}</p>
                            <button className="btn btn-secondary" onClick={handleManualCheckout}>Retry</button>
                        </div>
                    )
                )}
            </motion.div>

            {/* Support Modal */}
            <SupportModal isOpen={showSupportModal} onClose={() => setShowSupportModal(false)} />
        </div>
    )
}
